import {
  AdminSpinner,
  Disclaimer,
  Div,
  displayCentsInDollars,
  DollarHandIcon,
  H3,
  lightTheme,
  P,
  VGrubsLogoSmall,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  countOf,
  EXPENSE_LINES,
  INCOME_LINES,
  largestLine,
} from '../helpers/serviceValueLines';
import {
  marginTag,
  partColor,
  partSegments,
  partsTotal,
  profitBar,
  valueParts,
} from '../helpers/valueParts';
import { useLocationServiceValue } from '../hooks/useLocationServiceValue';
import { ClientValueModal } from './ClientValueModal';
import { CompanyValueModal } from './CompanyValueModal';
import { CardsGrid } from './ServiceValueCards.styled';
import { ValueCard } from './ValueCard';

type Props = {
  locationId: number | string;
};

/**
 * All time, under a heading of their own above the Overview, whose date
 * picker does not touch them: what the client made and saved with us, and
 * what we made from the client. Each card is the short version - a headline,
 * its parts and a bar of how they add up - and its arrow opens the whole
 * breakdown.
 *
 * Admin and support only - the screen does not render it for anyone else.
 */
export const ServiceValueCards = ({ locationId }: Props) => {
  const { value, isFetching, isError } = useLocationServiceValue({
    locationId,
    enabled: true,
  });
  const [open, setOpen] = useState<'client' | 'company' | null>(null);

  if (!value) {
    return isError ? (
      <P $m="0 0 24px" $color={lightTheme.lightGray}>
        Could not load what the client made with us.
      </P>
    ) : null;
  }

  const { client, company } = value;
  const parts = valueParts(client);
  const topIncome = largestLine(INCOME_LINES, company.income);
  const topExpense = largestLine(EXPENSE_LINES, company.expenses);
  const loss = company.profit < 0;

  return (
    <>
      <Div $flex $alignCenter $gap="8px" $m="0 0 12px">
        <H3 $m="0">Performance</H3>
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          All time
        </Disclaimer>
      </Div>
      <CardsGrid>
        {isFetching && (
          <AdminSpinner small absolute overflowBorderRadius="20px" creme />
        )}

        <ValueCard
          title="Client Made & Saved"
          value={displayCentsInDollars(client.total)}
          valueColor={lightTheme.green}
          icon={<DollarHandIcon />}
          iconBackground={lightTheme.green}
          stats={[
            {
              label: 'Brands',
              amount: client.payout,
              hint: `Payout on ${countOf(
                client.orders,
                'order'
              )}, ${displayCentsInDollars(client.sales)} in sales`,
              dot: partColor('payout'),
            },
            {
              label: 'vDrive Savings',
              amount: client.vDriveSavings,
              hint: countOf(client.vDriveDeliveries, 'delivery', 'deliveries'),
              dot: partColor('vDriveSavings'),
            },
            {
              label: 'Revenue Recovery',
              amount: client.recoveries,
              hint: `${countOf(client.recoveredOrders, 'order')} recovered`,
              dot: partColor('recoveries'),
            },
          ]}
          bar={{ segments: partSegments(parts), scale: partsTotal(parts) }}
          onOpen={() => setOpen('client')}
        />

        <ValueCard
          title="vGrubs Profit"
          value={displayCentsInDollars(company.profit)}
          // Negative is possible: the location costs us more than it brings.
          valueColor={loss ? lightTheme.primary : lightTheme.green}
          icon={<VGrubsLogoSmall />}
          tag={marginTag(company)}
          stats={[
            {
              label: 'Income',
              amount: company.income.total,
              hint: topIncome ? `Mostly ${topIncome.label}` : 'No income yet',
              // The bar is the income, the grey part what the costs take of
              // it. On a loss it turns round: the costs, the income the grey
              // part of them.
              dot: loss ? lightTheme.lightGray : lightTheme.green,
            },
            {
              label: 'Expenses',
              amount: company.expenses.total,
              hint: topExpense ? `Mostly ${topExpense.label}` : 'No expenses',
              dot: loss ? undefined : lightTheme.lightGray,
            },
          ]}
          bar={profitBar(company)}
          onOpen={() => setOpen('company')}
        />

        <ClientValueModal
          isOpen={open === 'client'}
          onClose={() => setOpen(null)}
          value={value}
        />
        <CompanyValueModal
          isOpen={open === 'company'}
          onClose={() => setOpen(null)}
          value={value}
        />
      </CardsGrid>
    </>
  );
};
